import * as coreClient from "@azure/core-client";
import * as Parameters from "./models/parameters";
import * as Mappers from "./models/mappers";
import {
  HostOptions,
  CheckoutAPIOptionalParams,
  BasketCreateOptionalParams,
  BasketCreateResponse,
  GetBasketByIDOptionalParams,
  GetBasketByIDResponse,
  DeleteBasketByIDOptionalParams,
  DeleteBasketByIDResponse,
  BasketAddItemOptionalParams,
  BasketAddItemResponse,
  BasketUpdateItemOptionalParams,
  BasketUpdateItemResponse,
  BasketDeleteItemOptionalParams,
  BasketDeleteItemResponse,
  BasketCheckoutOptionalParams,
  BasketCheckoutOperationResponse,
  ApplicantImageUploadOptionalParams,
  ApplicantImageUploadOperationResponse,
  ApplicantImageOptionalParams,
  ApplicantImageResponse,
  DirectDebitAccountVerifyOptionalParams,
  DirectDebitAccountVerifyResponse,
  PostcodeTicketVerifyOptionalParams,
  PostcodeTicketVerifyOperationResponse
} from "./models";

export class CheckoutAPI extends coreClient.ServiceClient {
  $host: HostOptions;

  /**
   * Initializes a new instance of the CheckoutAPI class.
   * @param options The parameter options
   */
  constructor(options?: CheckoutAPIOptionalParams) {
    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: CheckoutAPIOptionalParams = {
      requestContentType: "application/json; charset=utf-8"
    };

    const packageDetails = `azsdk-js-checkoutAPI/1.0.0-beta.1`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix
      },
      baseUri:
        options.endpoint ??
        options.baseUri ??
        "https://dt8nxbus.api.nationalexpress.com"
    };
    super(optionsWithDefaults);

    // Assigning values to Constant parameters
    this.$host = options.$host || "https://dt8nxbus.api.nationalexpress.com";
  }

  /**
   * Create New Basket
   * @param options The options parameters.
   */
  basketCreate(
    options?: BasketCreateOptionalParams
  ): Promise<BasketCreateResponse> {
    return this.sendOperationRequest({ options }, basketCreateOperationSpec);
  }

  /**
   * Get Basket By ID with Discount Details
   * @param id Basket Id
   * @param options The options parameters.
   */
  getBasketByID(
    id: string,
    options?: GetBasketByIDOptionalParams
  ): Promise<GetBasketByIDResponse> {
    return this.sendOperationRequest(
      { id, options },
      getBasketByIDOperationSpec
    );
  }

  /**
   * Delete Basket By ID (Use Request content-type as application/json )
   * @param id Basket Id
   * @param options The options parameters.
   */
  deleteBasketByID(
    id: string,
    options?: DeleteBasketByIDOptionalParams
  ): Promise<DeleteBasketByIDResponse> {
    return this.sendOperationRequest(
      { id, options },
      deleteBasketByIDOperationSpec
    );
  }

  /**
   * Basket Add Item With Checks For Discount If Voucher Code is Provided
   * @param id Basket Id
   * @param options The options parameters.
   */
  basketAddItem(
    id: string,
    options?: BasketAddItemOptionalParams
  ): Promise<BasketAddItemResponse> {
    return this.sendOperationRequest(
      { id, options },
      basketAddItemOperationSpec
    );
  }

  /**
   * Basket Update Item With Checks For Discount If Voucher Code is Provided
   * @param id Basket Id
   * @param itemId Basket Item Id
   * @param options The options parameters.
   */
  basketUpdateItem(
    id: string,
    itemId: number,
    options?: BasketUpdateItemOptionalParams
  ): Promise<BasketUpdateItemResponse> {
    return this.sendOperationRequest(
      { id, itemId, options },
      basketUpdateItemOperationSpec
    );
  }

  /**
   * Basket Delete Item  With Checks For Discount If Voucher Code is Provided (Use Request content-type
   * as application/json )
   * @param id Basket Id
   * @param itemId Basket Item Id
   * @param options The options parameters.
   */
  basketDeleteItem(
    id: string,
    itemId: number,
    options?: BasketDeleteItemOptionalParams
  ): Promise<BasketDeleteItemResponse> {
    return this.sendOperationRequest(
      { id, itemId, options },
      basketDeleteItemOperationSpec
    );
  }

  /**
   * Basket Checkout With Checks For Discount
   * @param id Basket Id
   * @param openIDToken The OpenID Token passed from Azure AD B2C
   * @param options The options parameters.
   */
  basketCheckout(
    id: string,
    openIDToken: string,
    options?: BasketCheckoutOptionalParams
  ): Promise<BasketCheckoutOperationResponse> {
    return this.sendOperationRequest(
      { id, openIDToken, options },
      basketCheckoutOperationSpec
    );
  }

  /**
   * Applicant Image Upload
   * @param basketId Basket Id (UUID Unique for Each Basket)
   * @param itemId Basket Item Id (Ticket Product ID)
   * @param quantityIndex Basket Item Quantity Index (Ticket Product Quantity Index)
   * @param options The options parameters.
   */
  applicantImageUpload(
    basketId: string,
    itemId: string,
    quantityIndex: number,
    options?: ApplicantImageUploadOptionalParams
  ): Promise<ApplicantImageUploadOperationResponse> {
    return this.sendOperationRequest(
      { basketId, itemId, quantityIndex, options },
      applicantImageUploadOperationSpec
    );
  }

  /**
   * View Applicant Image
   * @param basketId Basket Id (UUID Unique for Each Basket)
   * @param itemId Basket Item Id (Ticket Product ID)
   * @param quantityIndex Basket Item Quantity Index (Ticket Product Quantity Index)
   * @param options The options parameters.
   */
  applicantImage(
    basketId: string,
    itemId: string,
    quantityIndex: number,
    options?: ApplicantImageOptionalParams
  ): Promise<ApplicantImageResponse> {
    return this.sendOperationRequest(
      { basketId, itemId, quantityIndex, options },
      applicantImageOperationSpec
    );
  }

  /**
   * Direct-Debit Bank Account Verify
   * @param options The options parameters.
   */
  directDebitAccountVerify(
    options?: DirectDebitAccountVerifyOptionalParams
  ): Promise<DirectDebitAccountVerifyResponse> {
    return this.sendOperationRequest(
      { options },
      directDebitAccountVerifyOperationSpec
    );
  }

  /**
   * Postcode Ticket Mapping Verify
   * @param options The options parameters.
   */
  postcodeTicketVerify(
    options?: PostcodeTicketVerifyOptionalParams
  ): Promise<PostcodeTicketVerifyOperationResponse> {
    return this.sendOperationRequest(
      { options },
      postcodeTicketVerifyOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const basketCreateOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/create",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.Basket
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const getBasketByIDOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Basket
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [Parameters.accept],
  serializer
};
const deleteBasketByIDOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}",
  httpMethod: "DELETE",
  responses: {
    200: {
      bodyMapper: Mappers.BasketDeleteResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [Parameters.accept],
  serializer
};
const basketAddItemOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}/items",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.Basket
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const basketUpdateItemOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}/items/{itemId}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.Basket
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body1,
  urlParameters: [Parameters.$host, Parameters.id, Parameters.itemId],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const basketDeleteItemOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}/items/{itemId}",
  httpMethod: "DELETE",
  responses: {
    200: {
      bodyMapper: Mappers.Basket
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host, Parameters.id, Parameters.itemId],
  headerParameters: [Parameters.accept],
  serializer
};
const basketCheckoutOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/basket/{id}/checkout",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.BasketCheckoutResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.openIDToken
  ],
  mediaType: "json",
  serializer
};
const applicantImageUploadOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/applicant/photo",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ApplicantImageUploadResponse,
      headersMapper: Mappers.CheckoutAPIApplicantImageUploadHeaders
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  formDataParameters: [Parameters.image],
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.contentType1,
    Parameters.accept1,
    Parameters.basketId,
    Parameters.itemId1,
    Parameters.quantityIndex
  ],
  serializer
};
const applicantImageOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/applicant/photo",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "Stream" }, serializedName: "parsedResponse" }
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.basketId,
    Parameters.itemId1,
    Parameters.quantityIndex,
    Parameters.accept2
  ],
  serializer
};
const directDebitAccountVerifyOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/ddaccount/verify",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.DDAccountVerifyResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body3,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const postcodeTicketVerifyOperationSpec: coreClient.OperationSpec = {
  path: "/checkout/V1/postcode-ticket/verify",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.PostcodeTicketVerifyResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body4,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
